// Design tokens (--cxy-color-* etc.) that Dialog/Authentication's Tailwind classes (e.g. `bg-primary`
// -> var(--cxy-color-green)) resolve against. Elsewhere in atlas this is only pulled in by the
// landing-page-specific components (LandingPageHeader/Footer, MobileBottomNav), which don't render on
// most pages — product-plus loads it unconditionally instead (every page's Header imports it via
// `../cxy/cxy.ts`). Without it here, non-plus pages have no tokens defined at all, so the modal's
// cxy-color-based colors (the LOG IN button, social buttons) silently fall back to unstyled defaults.
import '@fpsm/cxy-tokens/tokens.css';

import { I18nextProvider, initReactI18next } from 'react-i18next';
import { useLoaderData, useLocation, useRouteError, useRouteLoaderData } from 'react-router';
import RootErrorPage, { DEFAULT_RESOURCES_URL, extractException } from '@cx-web/components/composites/RootErrorPage';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { BetslipProvider } from '@cx-web/feature-betslip/context';
import { FeatureFlagProvider } from '@cx-web/feature-flags/provider';
import { GeolocationProvider } from '@cx-web/feature-geolocation/context';
import { LabelProvider } from '@cx-web/feature-i18n';
import { MyOCAuthProvider } from '@cx-web/feature-myoc';
import {
  Authentication,
  AuthModalProvider,
  Dialog as AuthDialog,
  DialogContent,
  DialogTitle,
  useAuthModal,
  useAuthModalFromQueryParams,
} from '@cx-web/feature-myoc-login';
import { RepubProvider } from '@cx-web/feature-repub';
import { SiteConfigProvider } from '@cx-web/feature-site-config';
import { getRepubFromURL, isErrorWithStatus, updateI18nSingleton } from '@cx-web/utils';
import { captureException } from '@sentry/react-router';
import { createInstance } from 'i18next';

import { type loader as rootLoader } from '~/root-loader';
import tailwindStylesheet from '~/tailwind.css?url';

import type { Route } from './+types/route';
import { shouldCaptureRouteError } from './utils/shouldCaptureRouteError';
import Body from './body';
import Head from './head';
import { type loader } from './loader.server';

// Re-export loader directly from loader.server, meta and handle from implementation
export { loader } from './loader.server';
export { meta, handle } from './route-impl';

// Atlas's own compiled Tailwind output (base/components/utilities) was never wired into this
// document after `dcb945211` ("fix: move tailwind to `plus` routes", 2024) moved it out of the
// shared root in favour of the (now-separate) product-plus app. No non-plus page needed raw
// Tailwind utility classes since — everything else here is CSS Modules or prebuilt package
// dist CSS — until EU-5559 reused `@cx-web/feature-myoc-login`'s `Dialog` (which styles itself
// with plain Tailwind classes like `fixed`/`z-50`) outside product-plus. Without this link,
// atlas's tailwind.config.ts/postcss.config.js compile correctly but the output is never
// requested, so `.fixed` etc. don't exist in any stylesheet on the page — mirrors product-plus's
// `apps/product-plus/app/pages/layout.tsx` `links` export for the same file.
export const links: Route.LinksFunction = () => [{ rel: 'stylesheet', href: tailwindStylesheet }];

/**
 * Shared root error boundary. It must never fail: it reads only the lightweight
 * `'root'` loader (repub from the OC-Repub header, resourcesUrl from env) and falls
 * back to the URL-derived repub and the CDN default when that data is absent — which
 * is exactly when an API caused the error. RootErrorPage renders the whole document
 * from those inputs alone.
 */
export function ErrorBoundary() {
  const error = useRouteError();
  const errorStatus = isErrorWithStatus(error) ? error.status : null;
  // Expected 4xx route responses (e.g. a CMS data-template miss → 404, or an unrouted POST to a
  // non-/api/ path falling through to this catch-all where RR7 throws a 405) are rendered by
  // RootErrorPage below and are not actionable; only genuine failures belong in Sentry. This mirrors
  // the entry.server.tsx handleError suppression (NGF-6627), the other server-side Sentry path.
  if (shouldCaptureRouteError(errorStatus)) {
    captureException(extractException(error));
  }

  const rootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const location = useLocation();
  const repub = rootLoaderData?.repub ?? getRepubFromURL(new URL(`https://www.oddschecker.com${location.pathname}`));

  return (
    <RootErrorPage
      errorStatus={errorStatus}
      repub={repub}
      resourcesUrl={rootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL}
    />
  );
}

const i18n = createInstance();
i18n.use(initReactI18next);

/**
 * Renders the shared auth modal (EU-5559) for non-plus atlas pages. Must render inside
 * AuthModalProvider, and inside `<body>` (it renders a modal `<div>`, invalid as a direct child of
 * `<html>`). `hideInfoPanel` drops the OC+-branded left panel — that's plus-only. `authBasePath`
 * points the forms at this app's own `/auth/*` routes (registered in app/routes.ts), not product-plus's
 * `/plus/auth/*` (a different app/server behind the proxy).
 */
function AuthModalDriver({
  currentUrl,
  googleID,
  resourcesUrl,
}: {
  currentUrl: string;
  googleID: string;
  resourcesUrl: string;
}) {
  const { isOpen, toggle, view } = useAuthModal();
  // Non-plus only (product-plus has its own OC+ auth entry points): auto-opens the modal at the
  // forgot-password view, pre-filling the email, when the URL carries `?auth_view=forgot-password`.
  useAuthModalFromQueryParams();

  return (
    <AuthDialog onOpenChange={toggle} open={isOpen}>
      <DialogContent aria-describedby={undefined}>
        <DialogTitle style={{ display: 'none' }}>Log in or Sign up</DialogTitle>
        <Authentication
          authBasePath="/auth"
          currentUrl={currentUrl}
          googleID={googleID}
          hideInfoPanel
          resourcesUrl={resourcesUrl}
          view={view}
        />
      </DialogContent>
    </AuthDialog>
  );
}

function PageTemplate() {
  const rootLoaderData = useLoaderData<typeof loader>();
  const appRootLoaderData = useRouteLoaderData<typeof rootLoader>('root');
  const resourcesUrl = appRootLoaderData?.resourcesUrl ?? DEFAULT_RESOURCES_URL;
  if (!rootLoaderData) {
    throw new Error('Root loader data is missing');
  }

  const {
    ENV,
    featureToggleDefinitions,
    geolocationInfo,
    labels,
    lng,
    myocConfig,
    myocData,
    oddsFormat,
    persistedBetslipData,
    repub,
    resources,
    siteConfig,
    socialLoginIds,
    url,
  } = rootLoaderData;

  updateI18nSingleton(i18n, lng, resources);

  return (
    <I18nextProvider i18n={i18n}>
      <html lang={lng}>
        <FeatureFlagProvider featureToggleDefinitions={featureToggleDefinitions}>
          <RepubProvider repub={repub}>
            <MyOCAuthProvider myocConfig={myocConfig} myocData={myocData as MyOCData}>
              <GeolocationProvider countryCode={geolocationInfo.countryCode} state={geolocationInfo.subdivisionCode}>
                <SiteConfigProvider siteConfig={siteConfig}>
                  <LabelProvider devEnv={ENV.NODE_ENV !== 'production'} labels={labels}>
                    <BetslipProvider
                      bookmakers={siteConfig.bookmakers}
                      countryCode={geolocationInfo.countryCode}
                      persistedBetslipData={persistedBetslipData}
                      repub={repub}
                      subdivisionCode={geolocationInfo.subdivisionCode}
                      userOddsFormat={oddsFormat}
                    >
                      {/* Framework-agnostic auth-modal seam (NGF-6331, EU-5559). Authentication (the
                          shared myoc-login modal, formerly product-plus-only) renders the login/signup/
                          forgot-password UI from the store, hideInfoPanel'd for non-plus pages. */}
                      <AuthModalProvider>
                        <Head />
                        <Body>
                          {/* Must render inside <body>: the modal is a <div>, invalid under <html>. */}
                          <AuthModalDriver
                            currentUrl={url}
                            googleID={socialLoginIds?.googleID ?? ''}
                            resourcesUrl={resourcesUrl}
                          />
                        </Body>
                      </AuthModalProvider>
                    </BetslipProvider>
                  </LabelProvider>
                </SiteConfigProvider>
              </GeolocationProvider>
            </MyOCAuthProvider>
          </RepubProvider>
        </FeatureFlagProvider>
      </html>
    </I18nextProvider>
  );
}

export default PageTemplate;
